import type { ReactNode } from 'react';
import type { StatusDotProps } from 'elowen-plugin-ui-kit';
import { cn } from '../../lib/utils';

/** The pieces a drawer is made of besides its sections: the status dot and kind line in the header, and
 *  the property group that carries a record's facts.
 *
 *  `StatusDot` is also published to plugins (UI API 57). `PropertyGroup` and `PropertyRow` belong to the
 *  single-record drawers only (memory record, problem report, project models and similar): a window with
 *  sections draws its content from the Settings primitives instead (`SettingsSurface.tsx`).
 *
 *  Colour means state and nothing else here: a dot is green, amber or grey, and a fact is a labelled row,
 *  never a coloured pill. */

type DotTone = 'success' | 'warning' | 'muted';

const DOT_TONE: Record<DotTone, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  muted: 'bg-muted-foreground',
};

/** A 6px state dot. Decorative: the word beside it (`DetailMeta` status label, a row value) carries the meaning. */
export function StatusDot({ tone }: StatusDotProps) {
  return <span aria-hidden className={cn('inline-block size-1.5 shrink-0 rounded-full', DOT_TONE[tone])} />;
}

/** The header meta line for `Modal` / `WorkspaceDetailRail` `description`: a status dot with its word, then
 *  "·", then the kind. The separator appears only when both parts exist. */
export function DetailMeta({ status, children }: { status?: { tone: DotTone; label: string }; children?: ReactNode }) {
  return (
    <>
      {status ? (
        <span className="inline-flex items-center gap-1.5">
          <StatusDot tone={status.tone} />
          {status.label}
        </span>
      ) : null}
      {status && children ? <span aria-hidden>·</span> : null}
      {children}
    </>
  );
}

interface PropertyGroupProps { children: ReactNode; className?: string }
interface PropertyRowProps { label?: string; end?: ReactNode; children?: ReactNode; className?: string }

/** One hairline group of `PropertyRow`s, set apart by air rather than dividers, as Settings rows are. */
export function PropertyGroup({ children, className }: PropertyGroupProps) {
  return (
    <div role="list" data-slot="property-group" className={cn('overflow-hidden rounded-md border border-border py-1.5', className)}>
      {children}
    </div>
  );
}

/** A row of a `PropertyGroup`: a muted label (optional), the value in the middle and muted trailing text
 *  (`end`) on the right. A row without `label` is free-form (a commit, a person, a setting). */
export function PropertyRow({ label, end, children, className }: PropertyRowProps) {
  return (
    <div role="listitem" data-slot="property-row" className={cn('flex min-h-11 items-center gap-3 px-3.5 py-2.5 text-card-title', className)}>
      {label ? <span className="w-24 shrink-0 text-meta text-muted-foreground sm:w-30">{label}</span> : null}
      <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
      {end ? <span className="ml-auto shrink-0 whitespace-nowrap text-meta text-muted-foreground tabular-nums">{end}</span> : null}
    </div>
  );
}

/** Code-shaped value such as a git branch: a mono chip with a hairline, not a coloured pill. */
export function CodeChip({ children }: { children: ReactNode }) {
  return <code className="rounded-md border border-border px-1.5 py-0.5 font-mono text-small text-foreground">{children}</code>;
}
